import { Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, IconButton, ListSubheader, MenuItem, Select } from '@mui/material';
import { add } from "date-fns";
import React, { ChangeEvent, ReactNode, createContext, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { ALLERGIES_PATH, CLASSROOMGROUPS_PATH, CLASSROOMS_PATH, SCHOOL_JOB_CATEGORIES_PATH, userOrdersPath, userProfilePath } from "../../../functions/src/admin/paths";
import { AllergenIconImg } from "../../api/allergenIcon";
import { normalizeAllergiesForStorage, personHasDefinedAllergies } from "../../api/allergenUtils";
import { toServerFromDate } from "../../../functions/src/api/dates";
import firebase, { auth, DataSnapshot } from "../../api/firebase";
import { useDatabase, useDatabaseVal, useList } from "../../api/firebaseHooks";
import { langContext } from "../../api/Lang";
import OkDlg from "../../api/OkDlg";
import YesNoDlg from "../../api/YesNoDlg";
import { useStrings } from "../../common-strings";
import { useUserProfileStrings } from "./userProfile-strings";
import { AllergensList, Order, OrderPerson, StaffMember, Student, UserProfile } from "../../types";
import { isOrderPersonEntryComplete, isProfileStudentRowFocusId, PROFILE_PERSONNEL_BLOCK_ID, PROFILE_STUDENTS_SECTION_ID, profileStudentRowAnchorId } from "../validation";
import Allergies from "./Allergies";
import { Input } from "./inputs";

function cloneOrderPerson(val: OrderPerson | null | undefined): OrderPerson {
    if (!val) {
        return { firstName: '', lastName: '' };
    }
    return JSON.parse(JSON.stringify(val)) as OrderPerson;
}

/** RTDB set: use `allergies: null` to remove the node when the user cleared allergies in the dialog. */
function personDraftForRtdbSet(draft: OrderPerson, shouldClearAllergies: boolean): OrderPerson {
    const payload = { ...draft } as OrderPerson & { allergies?: AllergensList | null };
    if (draft.allergies != null) {
        return payload;
    }
    if (shouldClearAllergies) {
        payload.allergies = null;
    } else {
        delete payload.allergies;
    }
    return payload;
}

/** Delete confirm / delete control: use the person's name, not job role or classroom labels. */
function orderPersonNameForDeleteConfirm(
    person: OrderPerson | undefined,
    strings: { newStudentPlaceholder: string; newStaffPlaceholder: string },
): string {
    const { firstName = '', lastName = '' } = person || {};
    const name = `${firstName} ${lastName}`.trim();
    if (name) {
        return name;
    }
    return person?.personnel ? strings.newStaffPlaceholder : strings.newStudentPlaceholder;
}

const StudentPersonDialogPaperContext = createContext<HTMLElement | null>(null);

function useStudentPersonDialogSelectMenuProps() {
    const dialogPaperEl = useContext(StudentPersonDialogPaperContext);

    return useMemo(() => {
        const paperSx = {
            maxWidth: { xs: '90vw', sm: '400px' },
            width: { xs: '90vw', sm: 'auto' },
            maxHeight: 280,
            boxSizing: 'border-box',
            '& .MuiMenuItem-root': {
                whiteSpace: 'normal',
                wordWrap: 'break-word',
                padding: { xs: '12px 16px', sm: '8px 16px' },
            },
            '& .MuiListSubheader-root': {
                position: 'sticky',
                top: 0,
                zIndex: 1,
                backgroundColor: 'var(--order_card_bg)',
            },
        };

        const menuProps = {
            disableScrollLock: true,
            anchorOrigin: { vertical: 'bottom' as const, horizontal: 'left' as const },
            transformOrigin: { vertical: 'top' as const, horizontal: 'left' as const },
            // MUI 9 Menu uses Popover positioning (not Popper); marginThreshold keeps the list inside the viewport.
            marginThreshold: 12,
            slotProps: {
                paper: {
                    className: 'student-person-edit-dialog__select-menu',
                    sx: paperSx,
                },
            },
        };

        if (!dialogPaperEl) {
            return menuProps;
        }

        return {
            ...menuProps,
            container: dialogPaperEl,
        };
    }, [dialogPaperEl]);
}

const studentPersonDialogSelectSx = {
    '& .MuiSelect-select': {
        fontFamily: '"Roboto"',
    },
};

type ClassroomGroupsDropdownProps = {
    snapshot: DataSnapshot;
    name: string;
    optionsPath: string;
    categoriesPath: string;
    labelId?: string;
    selectId?: string;
    value?: string;
    onValueChange?: (value: string) => void;
};

function ClassroomGroupsDropdown({ snapshot, name, optionsPath, categoriesPath, labelId: labelIdProp, selectId: selectIdProp, value: controlledValue, onValueChange }: ClassroomGroupsDropdownProps) {
    const profileStrings = useUserProfileStrings();
    const commonStrings = useStrings();
    const studentPersonDialogSelectMenuProps = useStudentPersonDialogSelectMenuProps();
    const [lang] = useContext(langContext);
    const optionGroups = useDatabase(categoriesPath, (ref) => ref.orderByChild('order'));
    const optionGroupsList = useList(optionGroups);
    const [options, optionsLoaded] = useDatabaseVal<Record<string, Record<string, unknown>>>(optionsPath);
    const generatedId = useId();
    const selectId = selectIdProp ?? generatedId;
    const labelId = labelIdProp ?? `${generatedId}-label`;

    const rawSelected = controlledValue !== undefined ? controlledValue : (snapshot.child(name).val() ?? '');
    const availableKeys = options ? Object.keys(options).filter((key) => key && key !== 'unknown') : [];
    const selected = rawSelected && availableKeys.includes(rawSelected) ? rawSelected : '';

    const classroomLabel = (key: string): string => {
        const row = options?.[key];
        if (!row) {
            return key;
        }
        const nameField = row.name as Record<string, string> | undefined;
        if (nameField?.[lang]) {
            return nameField[lang];
        }
        const flat = row[lang];
        return typeof flat === 'string' ? flat : key;
    };

    const onChange = (event: { target: { value: string } }) => {
        const next = event.target.value;
        if (onValueChange) {
            onValueChange(next);
            return;
        }
        if (next) {
            snapshot.ref.child(name).set(next);
        } else {
            snapshot.ref.child(name).remove();
        }
    };

    if (!optionsLoaded || !optionGroups) {
        return (
            <FormControl sx={{ width: '100%' }} required>
                <Select value="" disabled displayEmpty>
                    <MenuItem value="" disabled>
                        <span style={{ color: 'var(--secondary_text)' }}>{commonStrings.loading}</span>
                    </MenuItem>
                </Select>
            </FormControl>
        );
    }

    return (
        <FormControl sx={{ width: '100%' }} required>
            <Select
                id={selectId}
                aria-labelledby={labelId}
                value={selected}
                onChange={onChange}
                displayEmpty
                renderValue={(value) => {
                    if (!value) {
                        return (
                            <span style={{ color: 'var(--secondary_text)' }}>
                                {profileStrings.selectClassroomPlaceholder}
                            </span>
                        );
                    }
                    return classroomLabel(value);
                }}
                sx={studentPersonDialogSelectSx}
                MenuProps={studentPersonDialogSelectMenuProps}
            >
                <MenuItem value="" disabled>
                    <span style={{ color: 'var(--secondary_text)' }}>{profileStrings.selectClassroomPlaceholder}</span>
                </MenuItem>
                {optionGroupsList.map((optionGroup) => {
                    const groupKey = optionGroup.key;
                    const groupTitle = optionGroup.child('name').child(lang).val() ?? '';
                    const groupOptions = Object.entries(options ?? {}).filter(([, groupedOption]) => String(groupedOption.classroomGroup) === groupKey);
                    if (!groupOptions.length) {
                        return null;
                    }
                    return [
                        <ListSubheader key={`classroom-group-${groupKey}`} sx={{ fontWeight: 700, color: 'var(--black_txt)' }}>{groupTitle}</ListSubheader>,
                        ...groupOptions.map(([optionKey]) => (
                            <MenuItem key={optionKey} value={optionKey}>
                                {classroomLabel(optionKey)}
                            </MenuItem>
                        )),
                    ];
                })}
            </Select>
        </FormControl>
    );
}

// Custom dropdown for school job categories
function SchoolJobCategoriesDropdown({ snapshot, name, labelId: labelIdProp, selectId: selectIdProp, value: controlledValue, onValueChange }: { snapshot: DataSnapshot, name: string, labelId?: string, selectId?: string, value?: string, onValueChange?: (value: string) => void }) {
    const profileStrings = useUserProfileStrings();
    const commonStrings = useStrings();
    const studentPersonDialogSelectMenuProps = useStudentPersonDialogSelectMenuProps();
    const [lang] = useContext(langContext);
    const jobCategories = useDatabase(SCHOOL_JOB_CATEGORIES_PATH);
    const jobCategoriesList = useList(jobCategories);
    const generatedId = useId();
    const selectId = selectIdProp ?? generatedId;
    const labelId = labelIdProp ?? `${generatedId}-label`;

    const rawSelected = controlledValue !== undefined ? controlledValue : (snapshot.child(name).val() ?? '');

    // Validate that the selected value exists in available options
    const availableValues = jobCategoriesList?.map(category => category.key) || [];
    const selected = availableValues.includes(rawSelected) ? rawSelected : '';

    const onChange = (event: { target: { value: string } }) => {
        if (onValueChange) {
            onValueChange(event.target.value);
            return;
        }
        snapshot.ref.child(name).set(event.target.value);
    }

    // Show loading state if job categories aren't loaded yet
    if (!jobCategories || !jobCategoriesList) {
        return (
            <FormControl sx={{ width: '100%' }} required>
                <Select
                    value=""
                    disabled
                    displayEmpty
                >
                    <MenuItem value="" disabled>
                        <div>
                            <div style={{ color: 'var(--secondary_text)' }}>{commonStrings.loading}</div>
                        </div>
                    </MenuItem>
                </Select>
            </FormControl>
        );
    }

    const selectedRoleTitle = selected
        ? jobCategoriesList.find((category) => category.key === selected)?.child('title').child(lang).val() ?? ''
        : '';

    return (
        <FormControl sx={{ width: '100%' }} required>
            <Select
                id={selectId}
                aria-labelledby={labelId}
                value={selected}
                onChange={onChange}
                displayEmpty
                renderValue={(value) => {
                    if (!value) {
                        return (
                            <span style={{ color: 'var(--secondary_text)' }}>
                                {profileStrings.selectRolePlaceholder}
                            </span>
                        );
                    }
                    return selectedRoleTitle || String(value);
                }}
                sx={studentPersonDialogSelectSx}
                MenuProps={studentPersonDialogSelectMenuProps}
            >
                <MenuItem value="" disabled>
                    <div>
                        <div style={{ color: 'var(--secondary_text)' }}>{profileStrings.selectRolePlaceholder}</div>
                    </div>
                </MenuItem>
                {jobCategoriesList.map(category => {
                    const title = category.child('title').child(lang).val();
                    const description = category.child('description').child(lang).val();

                    return (
                        <MenuItem key={category.key} value={category.key}>
                            <div style={{ width: '100%' }}>
                                <div style={{ fontWeight: 500 }}>{title}</div>
                                {description && (
                                    <div style={{
                                        fontSize: '0.875rem',
                                        color: 'var(--secondary_text)',
                                        marginTop: '2px',
                                        whiteSpace: 'normal',
                                        wordWrap: 'break-word',
                                        lineHeight: '1.4'
                                    }}>
                                        {description}
                                    </div>
                                )}
                            </div>
                        </MenuItem>
                    );
                })}
            </Select>
        </FormControl>
    );
}

export type StudentsPersonalTabParts = {
    dialogs: ReactNode;
    personnelBlock: ReactNode;
    studentsSection: ReactNode;
};

type StudentsProps = {
    snapshot: DataSnapshot;
    children: (parts: StudentsPersonalTabParts) => ReactNode;
    profileValidationFocusId?: string | null;
    profileValidationErrorMessage?: string;
};

export default function Students({ snapshot, children, profileValidationFocusId, profileValidationErrorMessage }: StudentsProps) {

    const profileStrings = useUserProfileStrings();
    const commonStrings = useStrings();
    const [lang] = useContext(langContext);
    const { uid } = auth.currentUser;
    const [profile] = useDatabaseVal<UserProfile>(userProfilePath(uid));

    const [editStudent, setEditStudent] = useState<string>();
    const [addNewStudent, setAddNewStudent] = useState<boolean>(false);
    const [addingType, setAddingType] = useState<'student' | 'staff'>('student');

    const [dlgDeleteStudent, setDlgDeleteStudent] = useState(null);
    const [dlgDeleteStudentError, setDlgDeleteStudentError] = useState(false);

    // Determine if the person being deleted is a student or staff member
    const getPersonType = (personSnapshot: DataSnapshot<OrderPerson>) => {
        const person = personSnapshot?.val() as OrderPerson | undefined;
        const classroom = (person as Student)?.classroom;
        const isStudent = !!person?.student;
        return isStudent;
    };

    const isStudentBeingDeleted = dlgDeleteStudent ? getPersonType(dlgDeleteStudent) : true;

    const deleteStudentErrorDlgTitle = isStudentBeingDeleted
        ? profileStrings.deleteStudentErrorDlgTitleStudent
        : profileStrings.deleteStudentErrorDlgTitleStaff;
    const deleteStudentErrorDlgMsg = profileStrings.deleteStudentErrorDlgMsg;

    const classrooms = useDatabase(CLASSROOMS_PATH);
    const classroomGroups = useDatabase(CLASSROOMGROUPS_PATH);
    const jobCategories = useDatabase(SCHOOL_JOB_CATEGORIES_PATH);

    const deletePersonConfirmDlg = useMemo(() => {
        if (!dlgDeleteStudent) {
            return { title: '', msg: '', yesLabel: commonStrings.delete };
        }
        const person = dlgDeleteStudent.val() as OrderPerson | undefined;
        if (person?.personnel) {
            return {
                title: profileStrings.removePersonnelFromOrderingConfirmTitle,
                msg: profileStrings.removePersonnelFromOrderingConfirmMsg,
                yesLabel: profileStrings.removePersonnelFromOrderingConfirmYes,
            };
        }
        return {
            title: profileStrings.deletePersonRowAria(orderPersonNameForDeleteConfirm(person, profileStrings)),
            msg: commonStrings.areYouSure,
            yesLabel: commonStrings.delete,
        };
    }, [commonStrings.areYouSure, commonStrings.delete, dlgDeleteStudent, profileStrings]);

    const oneMonthAgo = toServerFromDate(add(new Date(), { months: -1 }));
    const lastMonthOrders = useDatabase(userOrdersPath(uid), ref => ref.orderByKey().startAt(oneMonthAgo));
    const lastMonthOrdersList: DataSnapshot<Order>[] = useList(lastMonthOrders);

    const studentsList: DataSnapshot<OrderPerson>[] = useList(snapshot);

    // Separate students from staff members
    const students = studentsList.filter(s => {
        const person = s.val() as OrderPerson | undefined;
        return !!person?.student;
    });

    const staff = studentsList.filter(s => !!s.val()?.personnel);

    const personnelMatchesProfile = useCallback((personSnap: DataSnapshot<OrderPerson>): boolean => {
        const person = personSnap.val() as OrderPerson | undefined;
        if (!person?.personnel || !profile?.firstName || !profile?.lastName) {
            return false;
        }
        return person.firstName === profile.firstName && person.lastName === profile.lastName;
    }, [profile?.firstName, profile?.lastName]);

    const userPersonnelMember = useMemo((): DataSnapshot<OrderPerson> | undefined => {
        return staff.find((personSnap) => {
            if (!personnelMatchesProfile(personSnap)) {
                return false;
            }
            return isOrderPersonEntryComplete(personSnap.val(), false);
        });
    }, [staff, personnelMatchesProfile]);

    const userPersonnelDraft = useMemo((): DataSnapshot<OrderPerson> | undefined => {
        return staff.find((personSnap) => {
            if (!personnelMatchesProfile(personSnap)) {
                return false;
            }
            return !isOrderPersonEntryComplete(personSnap.val(), false);
        });
    }, [staff, personnelMatchesProfile]);

    const isUserPersonnel = !!userPersonnelMember;

    // Auto-update personnel entry when profile information changes
    useEffect(() => {
        if (userPersonnelMember && profile?.firstName && profile?.lastName) {
            const person = userPersonnelMember.val() as OrderPerson | undefined;
            if (person && (person.firstName !== profile.firstName || person.lastName !== profile.lastName)) {
                // Update the personnel entry with new profile information
                userPersonnelMember.ref.update({
                    firstName: profile.firstName,
                    lastName: profile.lastName
                });
            }
        }
    }, [profile?.firstName, profile?.lastName, userPersonnelMember]);

    // Drop incomplete student/personnel rows when not being edited (abandoned drafts).
    useEffect(() => {
        if (editStudent) {
            return;
        }
        studentsList.forEach((personSnap) => {
            const person = personSnap.val() as OrderPerson | undefined;
            if (!person?.student && !person?.personnel) {
                return;
            }
            const isStudent = !!person.student;
            if (!isOrderPersonEntryComplete(person, isStudent)) {
                personSnap.ref.remove();
            }
        });
    }, [editStudent, studentsList]);

    // Reusable function to check if a person has submitted orders in the last month
    const hasLastMonthOrders = (personKey: string) => {
        return lastMonthOrdersList.some(order => {
            const orderData = order.val();
            // Only check submitted/completed orders (has price and timestamp)
            const isSubmittedOrder = orderData.price !== undefined && orderData.timestamp !== undefined;

            if (!isSubmittedOrder) {
                return false; // Ignore incomplete orders
            }

            // For submitted orders, check both meals and snacks
            const hasMeals = orderData.meals && Object.values(orderData.meals).some(meal => meal.recipient === personKey);
            const hasSnacks = orderData.snacks && Object.values(orderData.snacks).some(snack => snack.recipient === personKey);
            return hasMeals || hasSnacks;
        });
    };

    const handlePersonnelCheckboxChange = (checked: boolean) => {
        if (checked) {
            if (userPersonnelMember) {
                return;
            }
            if (userPersonnelDraft) {
                setAddNewStudent(true);
                setAddingType('staff');
                setEditStudent(userPersonnelDraft.key);
                return;
            }

            // Create a new personnel member if no existing one found
            const newPersonnelKey = snapshot.ref.push().key;
            if (newPersonnelKey && profile?.firstName && profile?.lastName) {
                snapshot.ref.child(newPersonnelKey).set({
                    firstName: profile.firstName,
                    lastName: profile.lastName,
                    personnel: true
                });
                // Open edit form for the new personnel member (draft: cancel removes until role is saved)
                setAddNewStudent(true);
                setAddingType('staff');
                setEditStudent(newPersonnelKey);
            }
        } else {
            // Same confirmation + delete flow as the personnel row delete control
            const personnelToRemove = userPersonnelMember ?? userPersonnelDraft;
            if (personnelToRemove) {
                setDlgDeleteStudent(personnelToRemove);
            }
        }
    };

    const onAddNew = (type: 'student' | 'staff') => {
        setAddNewStudent(true);
        setAddingType(type);
        const newKey = snapshot.ref.push().key;
        if (newKey) {
            // Create entry with only the appropriate flag
            const entryData = { 
                [type === 'student' ? 'student' : 'personnel']: true 
            };
            
            snapshot.ref.child(newKey).set(entryData);
        }
        setEditStudent(newKey);
    }
    const onEditStudent = (studentKey: string) => {
        setAddNewStudent(false);
        setEditStudent(studentKey);
    }

    const deleteStudent = () => {
        if (hasLastMonthOrders(dlgDeleteStudent.key))
            setDlgDeleteStudentError(true);
        else
            dlgDeleteStudent.ref.remove();

        setDlgDeleteStudent(null);
    }

    const editDialogCancelRef = useRef<(() => void) | null>(null);
    const editDialogSaveRef = useRef<(() => void) | null>(null);
    const [editDialogCanSave, setEditDialogCanSave] = useState(false);
    const [editDialogPaperEl, setEditDialogPaperEl] = useState<HTMLElement | null>(null);

    const closeEditDialog = useCallback((): void => {
        setEditStudent(undefined);
        setAddNewStudent(false);
        setEditDialogCanSave(false);
        editDialogCancelRef.current = null;
        editDialogSaveRef.current = null;
    }, []);

    const editPersonSnapshot = useMemo((): DataSnapshot | null => {
        if (!editStudent) {
            return null;
        }
        return snapshot.child(editStudent);
    }, [editStudent, snapshot]);

    const editDialogTitle = useMemo((): string => {
        if (!editPersonSnapshot) {
            return '';
        }
        const person = editPersonSnapshot.val() as OrderPerson | undefined;
        const isStudent = addNewStudent ? addingType === 'student' : !!person?.student;
        return isStudent ? profileStrings.studentInfoHeading : profileStrings.staffInfoHeading;
    }, [addNewStudent, addingType, editPersonSnapshot, profileStrings.staffInfoHeading, profileStrings.studentInfoHeading]);

    const renderPersonList = (personList: DataSnapshot<OrderPerson>[], isStudentSection: boolean) => {
        return personList
            .filter(s_student => {
                const person = s_student.val() as OrderPerson | undefined;
                if (!person?.student && !person?.personnel) {
                    return false;
                }
                const isStudent = !!person.student;
                return isOrderPersonEntryComplete(person, isStudent);
            })
            .map(s_student => {
                const person = s_student.val() as OrderPerson | undefined;
                const { firstName = '', lastName = '' } = person || {};
                const classroom = (person as Student)?.classroom;
                const isStudent = !!person?.student;
                const isPersonnel = !!person?.personnel;

                const classRoomGroupName = isStudent ? classroomGroups?.child(String(classrooms?.child(`${classroom}/classroomGroup`).val())).child(`name/${lang}`).val() ?? '' : '';
                const classRoomName = isStudent ? classrooms?.child(`${classroom}/name/${lang}`).val() : '';
                const userClassroom = isStudent && !!classRoomName ? !!classRoomGroupName ? ` - ${classRoomName} (${classRoomGroupName})` : ` - ${classRoomName}` : '';

                const allergies = s_student.child(ALLERGIES_PATH).val() as AllergensList | undefined;
                const hasDefinedAllergies = personHasDefinedAllergies(allergies);

                const displayName = isPersonnel
                    ? (() => {
                        const jobRoleKey = (person as StaffMember)?.jobRole;
                        const jobRoleTitle = jobRoleKey
                            ? jobCategories?.child(jobRoleKey).child('title').child(lang).val() ?? ''
                            : '';
                        return jobRoleTitle || profileStrings.newStaffPlaceholder;
                    })()
                    : firstName.trim() || lastName.trim()
                        ? `${firstName} ${lastName}`.trim()
                        : profileStrings.newStudentPlaceholder;

                const rowAriaLabel = `${profileStrings.editPersonRowAria}: ${displayName}${userClassroom}`;

                return <React.Fragment key={s_student.key}>
                    <div id={profileStudentRowAnchorId(s_student.key)} className="account-figma-student-row" tabIndex={-1}>
                        <div className="account-figma-student-row__main">
                            <span className="account-figma-student-row__name">
                                <span className="account-figma-student-row__name-text">
                                    {isPersonnel ? (
                                        <>
                                            <strong className="account-figma-student-row__role-label">{profileStrings.roleInSchoolLabel}</strong>
                                            {' '}
                                            {displayName}
                                        </>
                                    ) : (
                                        <>{displayName}{userClassroom}</>
                                    )}
                                </span>
                                {hasDefinedAllergies && (
                                    <span className="account-figma-student-row__allergy-icon" aria-hidden="true">
                                        <AllergenIconImg
                                            alt=""
                                            width={18}
                                            height={18}
                                            variant="red"
                                        />
                                    </span>
                                )}
                            </span>
                        </div>
                        <div className="account-figma-student-row__actions">
                            <IconButton
                                type="button"
                                size="small"
                                className="account-figma-student-row__action account-figma-student-row__action--edit"
                                aria-label={rowAriaLabel}
                                onClick={() => { onEditStudent(s_student.key); }}
                            >
                                <img
                                    className="account-figma-student-row__edit-img"
                                    src="/img-assets/edit-orange.svg"
                                    alt=""
                                />
                            </IconButton>
                            {!isPersonnel && (
                                <IconButton
                                    type="button"
                                    size="small"
                                    className="account-figma-student-row__action account-figma-student-row__action--delete"
                                    aria-label={profileStrings.deletePersonRowAria(orderPersonNameForDeleteConfirm(person, profileStrings))}
                                    onClick={() => { setDlgDeleteStudent(s_student); }}
                                >
                                    <img
                                        className="account-figma-student-row__delete-img"
                                        src="/img-assets/delete-red.svg"
                                        alt=""
                                    />
                                </IconButton>
                            )}
                        </div>
                    </div>
                    <div className='separator account-figma-card__divider'></div>
                </React.Fragment>
            });
    }

    if (!snapshot)
        return <></>

    const dialogs = <>
        {dlgDeleteStudent &&
            <YesNoDlg title={deletePersonConfirmDlg.title} msg={deletePersonConfirmDlg.msg} yes={deletePersonConfirmDlg.yesLabel} no={commonStrings.cancel} onNo={() => setDlgDeleteStudent(null)} onYes={deleteStudent} />
        }
        {dlgDeleteStudentError &&
            <OkDlg title={deleteStudentErrorDlgTitle} msg={deleteStudentErrorDlgMsg} onClose={() => setDlgDeleteStudentError(false)} />
        }

        <Dialog
            open={!!editStudent}
            onClose={() => editDialogCancelRef.current?.()}
            maxWidth="md"
            fullWidth
            scroll="paper"
            className="student-person-edit-dialog"
            slotProps={{
                paper: {
                    ref: (paperEl: HTMLDivElement | null) => {
                        setEditDialogPaperEl(paperEl);
                    },
                },
            }}
        >
            <StudentPersonDialogPaperContext.Provider value={editDialogPaperEl}>
                <DialogTitle component="h2">{editDialogTitle}</DialogTitle>
                <DialogContent>
                    {editPersonSnapshot && (
                        <StudentData
                            snapshot={editPersonSnapshot}
                            isNewStudent={addNewStudent}
                            addingType={addingType}
                            onBindCancel={(cancel) => { editDialogCancelRef.current = cancel; }}
                            onBindSave={(save) => { editDialogSaveRef.current = save; }}
                            onCanSaveChange={setEditDialogCanSave}
                            onDone={closeEditDialog}
                        />
                    )}
                </DialogContent>
                <DialogActions className="student-person-edit-dialog__actions">
                    <Button color="primary" onClick={() => editDialogCancelRef.current?.()}>
                    {commonStrings.cancel2}
                </Button>
                <Button variant="contained" disableElevation disabled={!editDialogCanSave} onClick={() => editDialogSaveRef.current?.()}>
                    {addNewStudent ? commonStrings.done : commonStrings.save}
                </Button>
                </DialogActions>
            </StudentPersonDialogPaperContext.Provider>
        </Dialog>
    </>;

    const personnelValidationInvalid = profileValidationFocusId === PROFILE_PERSONNEL_BLOCK_ID;
    const studentsSectionValidationInvalid = profileValidationFocusId === PROFILE_STUDENTS_SECTION_ID || isProfileStudentRowFocusId(profileValidationFocusId);
    const personnelValidationErrorId = `${PROFILE_PERSONNEL_BLOCK_ID}-validation-error`;

    const personnelBlock = <>
        <div
            id={PROFILE_PERSONNEL_BLOCK_ID}
            tabIndex={-1}
            className={`account-figma-personnel account-figma-personnel--in-card${personnelValidationInvalid ? ' account-figma-personnel--validation-error' : ''}`}
            aria-invalid={personnelValidationInvalid ? true : undefined}
            aria-describedby={personnelValidationInvalid && profileValidationErrorMessage ? personnelValidationErrorId : undefined}
        >
            <FormControlLabel
                className="account-figma-personnel__control"
                sx={{
                    alignItems: 'flex-start',
                    marginLeft: 0,
                    marginRight: 0,
                    '& .MuiCheckbox-root': {
                        padding: '1px 9px 0',
                    },
                }}
                control={
                    <Checkbox
                        checked={isUserPersonnel}
                        onChange={(e) => handlePersonnelCheckboxChange(e.target.checked)}
                        color="primary"
                        slotProps={{
                            input: {
                                'aria-describedby': personnelValidationInvalid && profileValidationErrorMessage ? personnelValidationErrorId : undefined,
                            },
                        }}
                    />
                }
                label={profileStrings.personnelCheckboxLabel}
            />
            {personnelValidationInvalid && profileValidationErrorMessage && (
                <p id={personnelValidationErrorId} className="account-figma-field__validation-error account-figma-personnel__validation-error" role="alert">
                    {profileValidationErrorMessage}
                </p>
            )}
        </div>
        {isUserPersonnel && userPersonnelMember && (
            <>
                <div className='separator account-figma-card__divider'></div>
                {renderPersonList([userPersonnelMember], false)}
            </>
        )}
    </>;

    const studentsSection = <section id={PROFILE_STUDENTS_SECTION_ID} tabIndex={-1} className={studentsSectionValidationInvalid ? 'account-figma-section--validation-error' : undefined} aria-invalid={studentsSectionValidationInvalid ? true : undefined} aria-describedby={studentsSectionValidationInvalid && profileValidationErrorMessage ? `${PROFILE_STUDENTS_SECTION_ID}-validation-error` : undefined}>
        <h2 className="account-figma-section-heading">{profileStrings.studentsSectionHeading}</h2>
        {studentsSectionValidationInvalid && profileValidationErrorMessage && (
            <p id={`${PROFILE_STUDENTS_SECTION_ID}-validation-error`} className="account-figma-field__validation-error account-figma-section__validation-error" role="alert">
                {profileValidationErrorMessage}
            </p>
        )}
        <div className="account-figma-card">
            {renderPersonList(students, true)}
            <Button
                type="button"
                variant="text"
                color="primary"
                className="account-figma-add-student"
                onClick={() => onAddNew('student')}
            >
                <span aria-hidden="true" className="account-figma-add-student__plus">+</span>
                {profileStrings.addStudentLink}
            </Button>
        </div>
    </section>;

    return <>{children({ dialogs, personnelBlock, studentsSection })}</>;
}

type StudentDataProps = {
    snapshot: DataSnapshot;
    isNewStudent: boolean;
    addingType?: 'student' | 'staff';
    onDone: () => void;
    onBindCancel?: (cancel: () => void) => void;
    onBindSave?: (save: () => void) => void;
    onCanSaveChange?: (canSave: boolean) => void;
};

function StudentData({ snapshot, isNewStudent, addingType, onDone, onBindCancel, onBindSave, onCanSaveChange }: StudentDataProps) {

    const profileStrings = useUserProfileStrings();
    const commonStrings = useStrings();
    const classroomSelectId = useId();
    const classroomLabelId = `${classroomSelectId}-label`;
    const jobRoleSelectId = useId();
    const jobRoleLabelId = `${jobRoleSelectId}-label`;
    const [dlgStaffRoleRequired, setDlgStaffRoleRequired] = useState(false);
    const originalPersonRef = useRef<OrderPerson | undefined>(snapshot.val());
    const dialogPersonKey = snapshot.key ?? '';
    const [dialogDraft, setDialogDraft] = useState<OrderPerson>(() => (
        cloneOrderPerson(snapshot.val() as OrderPerson)
    ));
    const [dialogHadAllergiesInitially, setDialogHadAllergiesInitially] = useState(false);

    useEffect(() => {
        const initial = cloneOrderPerson(snapshot.val() as OrderPerson);
        if (initial.allergies != null && !personHasDefinedAllergies(initial.allergies)) {
            delete initial.allergies;
        }
        setDialogDraft(initial);
        originalPersonRef.current = snapshot.val();
        setDialogHadAllergiesInitially(snapshot.child(ALLERGIES_PATH).exists());
        // Reset when switching person only — not when `snapshot` identity changes (parent passes snapshot.child(key) each render).
    }, [dialogPersonKey]);

    const handleFieldChange = ({ target: { name, value } }: ChangeEvent<HTMLInputElement>): void => {
        setDialogDraft((prev) => ({ ...prev, [name]: value }));
    };

    const handleCancel = useCallback((): void => {
        if (isNewStudent) {
            snapshot.ref.remove();
        } else {
            const original = originalPersonRef.current;
            if (original != null) {
                snapshot.ref.set(original);
            }
        }
        onDone();
    }, [isNewStudent, onDone, snapshot.ref]);

    useEffect(() => {
        onBindCancel?.(handleCancel);
    }, [handleCancel, onBindCancel]);

    const onSave = useCallback((): void => {
        const current = dialogDraft as OrderPerson | undefined;
        const studentRole = isNewStudent ? addingType === 'student' : !!current?.student;

        if (!isOrderPersonEntryComplete(current, studentRole)) {
            return;
        }

        const { firstName = '', lastName = '' } = current || {};

        // If both names are empty, don't save (remove the entry if it was new)
        if (firstName.trim().length === 0 && lastName.trim().length === 0) {
            if (isNewStudent) {
                snapshot.ref.remove();
            }
            onDone();
            return;
        }

        // Check if staff member has job role selected
        const isStaff = !!current?.personnel;
        if (isStaff && !(current as StaffMember)?.jobRole) {
            setDlgStaffRoleRequired(true);
            return;
        }

        const draftToSave = { ...dialogDraft };
        const normalizedAllergies = normalizeAllergiesForStorage(draftToSave.allergies);
        if (normalizedAllergies === null) {
            delete draftToSave.allergies;
        } else if (normalizedAllergies !== undefined) {
            draftToSave.allergies = normalizedAllergies;
        }
        const shouldClearAllergies = dialogHadAllergiesInitially && draftToSave.allergies == null;
        snapshot.ref.set(personDraftForRtdbSet(draftToSave, shouldClearAllergies));

        onDone();
    }, [addingType, dialogDraft, dialogHadAllergiesInitially, isNewStudent, onDone, snapshot]);

    useEffect(() => {
        onBindSave?.(onSave);
    }, [onBindSave, onSave]);

    const classroom = (dialogDraft as Student)?.classroom;
    const isStudent = isNewStudent ? addingType === 'student' : !!dialogDraft?.student;

    const canSave = useMemo((): boolean => {
        const studentRole = isNewStudent ? addingType === 'student' : !!dialogDraft?.student;
        return isOrderPersonEntryComplete(dialogDraft, studentRole);
    }, [dialogDraft, isNewStudent, addingType]);

    useEffect(() => {
        onCanSaveChange?.(canSave);
    }, [canSave, onCanSaveChange]);

    return <>
        {dlgStaffRoleRequired && (
            <OkDlg title={commonStrings.errorTitle} msg={profileStrings.staffRoleErrorMsg} onClose={() => setDlgStaffRoleRequired(false)} />
        )}
        <div className="student-person-edit-dialog__form">
            <div className="student-person-edit-dialog__field">
                <Input
                    variant="figmaDialog"
                    name='firstName'
                    label={isStudent ? profileStrings.studentNameLabel : profileStrings.staffNameLabel}
                    labelRequired={isStudent}
                    snapshot={snapshot}
                    value={dialogDraft.firstName ?? ''}
                    onChange={handleFieldChange}
                    disabled={!isStudent} // Disable for personnel members
                />
            </div>
            <div className="student-person-edit-dialog__field">
                <Input
                    variant="figmaDialog"
                    name='lastName'
                    label={isStudent ? profileStrings.studentLastNameLabel : profileStrings.staffLastNameLabel}
                    labelRequired={isStudent}
                    snapshot={snapshot}
                    value={dialogDraft.lastName ?? ''}
                    onChange={handleFieldChange}
                    disabled={!isStudent} // Disable for personnel members
                />
            </div>
            {isStudent && (
                <div className="student-person-edit-dialog__field">
                    <div className="student-person-edit-dialog__field-label-wrap user-role">
                        <span id={classroomLabelId} className="account-figma-field__label account-figma-field__label--required">
                            {profileStrings.studentClassroomLabel}
                        </span>
                        <div className="student-person-edit-dialog__dropdown student-person-edit-dialog__dropdown--classroom drop-down-input">
                            <ClassroomGroupsDropdown snapshot={snapshot} name="classroom" optionsPath={CLASSROOMS_PATH} categoriesPath={CLASSROOMGROUPS_PATH} selectId={classroomSelectId} labelId={classroomLabelId} value={classroom ?? ''} onValueChange={(value) => {
                                setDialogDraft((prev) => {
                                    const next = { ...prev } as Student;
                                    if (value) {
                                        next.classroom = value;
                                    } else {
                                        delete next.classroom;
                                    }
                                    return next;
                                });
                            }} />
                        </div>
                    </div>
                </div>
            )}
            {!isStudent && (
                <div className="student-person-edit-dialog__field">
                    <div className="student-person-edit-dialog__field-label-wrap user-role">
                        <span id={jobRoleLabelId} className="account-figma-field__label account-figma-field__label--required">
                            {profileStrings.roleInSchoolLabel}
                        </span>
                        <div className="student-person-edit-dialog__dropdown drop-down-input">
                            <SchoolJobCategoriesDropdown snapshot={snapshot} name='jobRole' labelId={jobRoleLabelId} selectId={jobRoleSelectId} value={(dialogDraft as StaffMember).jobRole ?? ''} onValueChange={(value) => {
                                setDialogDraft((prev) => ({ ...prev, jobRole: value }));
                            }} />
                        </div>
                    </div>
                </div>
            )}
            <Allergies allergiesValue={dialogDraft.allergies !== undefined ? dialogDraft.allergies : null} onAllergiesValueChange={(next) => {
                setDialogDraft((prev) => {
                    const updated = { ...prev };
                    if (next == null) {
                        delete updated.allergies;
                    } else {
                        updated.allergies = next;
                    }
                    return updated;
                });
            }} />
        </div>
    </>
}

